import i18next from 'i18next';
import { NESTED_PLATFORM_OPTIONS } from '../constants/filters';
import getGroupByOptions from '../insights/getGroupByOptions';
import {
    getSortByOptions,
    getOrderByOptions,
} from '../social-media-posts/dropdownOptions';
import { getCompareOptions } from '../components/filters/insights/getOptions';
/**
 * Returns an array of objects
 * with 'true' as the value for each element id
 * @param {Object} Filter object
 * @return {Object} Array of objects with ids and a truthy value
 */

export const setSelected = obj =>
    obj.selected?.reduce((acc, val) => {
        acc[val] = true;
        return acc;
    }, {});

/**
 * Gets all the truthy IDs from a given
 * Selected Filters object.
 * @param {Object} Filters with selected values Object
 * @return {Array} Array of selected filters IDs
 */

export const getSelected = obj =>
    Object.entries(obj)?.reduce((acc, [id, isSelected]) => {
        if (isSelected) acc.push(id);
        return acc;
    }, []);

/**
 * Gets all the truthy Placements ids from a given
 * Selected Rightsholders-Placements object.
 * @param {Object} Rightsholders-Placements pairs Object
 * @return {Array} Array of Placements IDs
 */
export const getSelectedPlacements = obj =>
    Object.entries(obj).reduce((acc, [id, isSelected]) => {
        if (isSelected) {
            const [rid, pids] = id.split('-');
            const ids = pids.split(',').map(pid => `${rid}-${pid}`);
            acc.push(...ids);
        }
        return acc;
    }, []);

/**
 * Gets all the truthy rightsholders ids from a given
 * Selected Rightsholders object.
 * @param {Object} Rightsholders-Leagues pairs Object
 * @return {Array} Array of RH IDs
 */
export const getSelectedNestedRightsholders = obj =>
    Object.entries(obj).reduce((acc, [id, isSelected]) => {
        const splittedIds = id.split('-');
        const newId = splittedIds[1] ? splittedIds[1] : id;
        if (isSelected && !acc.includes(newId)) acc.push(newId);
        return acc;
    }, []);

/**
 * Returns an array of strings containing the RH names
 * @param {Object} Rightsholders-Leagues pairs Object
 * @param {Object} Rightsholders Object
 * @return {Array} Array of RH names
 */
export const getRHLeagueNames = (rhsLPairs, rhs) => {
    return Object.entries(rhsLPairs).reduce((acc, [id, isSelected]) => {
        const splittedIds = id.split('-');
        const newId = splittedIds[1] ? splittedIds[1] : id;
        if (isSelected && !acc.includes(newId) && newId) {
            const rh = rhs.list[parseInt(newId)];
            if (rh?.name) acc.push(rh.name);
        }
        return acc;
    }, []);
};

/**
 * Returns an array of strings containing the names
 * of the filter selected values
 * @param {Object} Filter Object
 * @return {Array} Array of Filter Values names
 */
export const getFilterValueNames = (filter, shouldParse = true) => {
    return filter.selected
        ?.map(filterId => {
            if (filterId) {
                const parsedId = shouldParse ? parseInt(filterId) : filterId;
                if (!filter?.list) return null;
                const filterOption = filter?.list[parsedId];
                return filterOption?.name;
            }
        })
        .filter(p => p);
};

/**
 * Returns an array of strings containing the names
 * of the placement selected values
 * @param {Object} Placement Options
 * @param {Object} Placement Selection
 * @return {Array} Array of Placement names
 */
export const getPlacementNames = ({
    options: plcOptions,
    selected: plcSelected,
    hasSeveralRightsholders,
}) => {
    const names = plcSelected.map(plc => {
        if (!Array.isArray(plcOptions)) return null;
        if (hasSeveralRightsholders) {
            const [rid, pids] = plc.split('-');
            const parent = plcOptions.find(o => `${o.value}` === `${rid}`);
            if (!parent?.nested) return null;

            const placementId = pids.includes(',') ? pids : parseInt(pids);
            const directMatch = parent.nested.find(
                n => n.value === placementId,
            );
            if (directMatch?.label) return directMatch.label;

            // When selected values are expanded as individual placement ids,
            // fallback to grouped text-exposure option labels.
            const groupedMatch = parent.nested.find(n => {
                const value = `${n.value}`;
                if (!value.includes(',')) return false;
                return value.split(',').includes(`${pids}`);
            });

            return groupedMatch?.label || null;
        } else {
            if (!plcOptions.find(o => o.value === plc)) return null;
            if (!plcOptions.find(o => o.value === plc).label) return null;
            return plcOptions.find(o => o.value === plc).label;
        }
    });

    return [...new Set(names.filter(Boolean))];
};

/**
 * Returns an array of strings containing the names
 * of the selected Platforms values
 * @param {Object} Platforms Object
 * @return {Array} Array of Platforms Values names
 */
export const getPlatformsNames = platforms => {
    return platforms.selected?.map(pltId => {
        if (pltId.includes('-')) {
            return NESTED_PLATFORM_OPTIONS[pltId];
        } else {
            return platforms.list[parseInt(pltId)].name;
        }
    });
};

/**
 * Returns an array of strings containing the names
 * of the selected Group By options
 * @param {Object} Group By Object
 * @return {Array} Array of Group By  Values names
 */
export const getGroupByNames = async (
    groupBy = [],
    isComparingDates = false,
) => {
    const t = i18next.getFixedT(null, 'common');
    let expandedGroupByOptions = [
        ...(await getGroupByOptions(t)),
        { label: t('groupByOptions.collection'), value: 'collection' },
        { label: t('groupByOptions.placementType'), value: 'placementType' },
        { label: t('groupByOptions.seasonType'), value: 'seasonType' },
        { label: t('groupByOptions.gameType'), value: 'gameType' },
        { label: t('groupByOptions.homeTeam'), value: 'homeTeam' },
        { label: t('groupByOptions.awayTeam'), value: 'awayTeam' },
    ];

    if (isComparingDates) {
        expandedGroupByOptions = expandedGroupByOptions.filter(
            o => o.value !== 'date',
        );
    }

    const names = groupBy
        .map(
            value => expandedGroupByOptions.find(o => o.value === value)?.label,
        )
        .filter(p => !!p);

    if (isComparingDates) {
        names.push(t('filters.dateRange'));
    }
    return names;
};

export const getSortBy = ({ sort, order }) => {
    const t = i18next.getFixedT(null, 'common');
    const translatedSortByOptions = getSortByOptions(t);
    const translatedOrderByOptions = getOrderByOptions(t, sort) || [];
    const so = translatedSortByOptions.find(s => s.value === sort);
    const sortName = so ? so.name : '';
    const oo = translatedOrderByOptions.find(s => s.value === order);
    const orderByName = oo ? oo.name : '';
    return `${sortName} - ${orderByName}`;
};

export const getComparedDate = ({ startDate, endDate, compareDates }) => {
    if (!compareDates?.option) return null;
    const t = i18next.getFixedT(null, 'measurement');
    const options = getCompareOptions({ startDate, endDate }, 'name', false, t);
    return options.find(o => o.value === compareDates.option)
        ? options.find(o => o.value === compareDates.option).name
        : null;
};

/**
 * Parses selected placements for flat or nested rightsholder structures,
 * using a flat array of selected placement IDs instead of an object map.
 *
 * @param {string[]} selected Array of selected placement IDs (e.g. ["1039-108", "1039-111"])
 * @param {Array} options List of placement options, possibly with nested values
 * @param {boolean} hasSeveralRightsholders Flag to distinguish flat vs nested selection logic
 * @returns {string[]} Array of selected placement IDs in "rid-pid" format
 */
export const parseSelectedPlacements = (
    selected,
    options,
    hasSeveralRightsholders,
) => {
    // Create a Set of selected placement IDs for efficient lookup
    const selectedPlacementSet = new Set(selected);

    const isPlacementSelected = (rightsholderId, placementGroup) => {
        const pids = placementGroup.toString().split(',');
        return pids.some(pid =>
            selectedPlacementSet.has(`${rightsholderId}-${pid}`),
        );
    };

    if (!hasSeveralRightsholders) {
        // Flat structure: options are in format { value: "1039-108" }
        return options
            .filter(option => {
                const [rid, pid] = option.value.split('-');
                return isPlacementSelected(rid, pid);
            })
            .map(option => option.value);
    } else {
        // Nested structure: options are grouped by rightsholder
        return options.flatMap(plc => {
            const rid = plc.value;
            return plc.nested
                .filter(placementGroup =>
                    isPlacementSelected(rid, placementGroup.value),
                )
                .map(p => `${rid}-${p.value}`);
        });
    }
};

/**
 * Filters and returns placement values from options that match the selected IDs.
 *
 * Supports both flat IDs (e.g. "1039-108") and grouped IDs
 * (e.g. "1039-108,111,116") in the `selected` array.
 *
 * @param {string[]} selected Array of selected placement IDs (flat or grouped)
 * @param {Array} options Placement option objects with `value` and `label`
 * @param {boolean} hasSeveralRightsholders Set to false — only supports flat structure
 * @returns {string[]} Filtered array of option values matching selected placements
 */
export const parseSelectedPlacementsOptions = (selected, options) => {
    // Step 1: Expand selected IDs into a Set for fast lookup
    const selectedSet = new Set();
    selected.forEach(entry => {
        if (!entry.includes('-')) return;
        const [rid, suffixes] = entry.split('-', 2);
        suffixes.split(',').forEach(pid => {
            selectedSet.add(`${rid}-${pid}`);
        });
    });

    // Step 2: Helper to check if any pid in a grouped value is selected
    const isPlacementSelected = value => {
        if (!value.includes('-')) return false;
        const [rid, suffixes] = value.split('-', 2);
        return suffixes
            .split(',')
            .some(pid => selectedSet.has(`${rid}-${pid}`));
    };

    // Step 3: Filter options where at least one pid is selected
    return options
        .filter(option => isPlacementSelected(option.value))
        .map(option => option.value);
};
